import React from 'react';
import { useTranslation } from 'react-i18next';
import { Task, Reminder } from '../../../../types/task';
import { format, parseISO } from 'date-fns';

interface TaskPropertyTagsProps {
  task?: Task | null;
  formData: any;
  onTimeClick: () => void;
  onReminderClick: () => void;
  onRecurrenceClick: () => void;
  onImportanceClick: () => void;
  onDeleteClick?: () => void;
}

/**
 * 任务属性标签横向滚动组件
 */
export const TaskPropertyTags: React.FC<TaskPropertyTagsProps> = ({
  task,
  formData,
  onTimeClick,
  onReminderClick,
  onRecurrenceClick,
  onImportanceClick,
  onDeleteClick
}) => {
  const { t } = useTranslation(['v3/tasks', 'common']);

  // 格式化时间显示
  const formatTime = (dateStr: string) => {
    try {
      const date = parseISO(dateStr);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);

      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const tomorrowOnly = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());
      const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

      let prefix = '';
      if (dateOnly.getTime() === todayOnly.getTime()) {
        prefix = '今天 ';
      } else if (dateOnly.getTime() === tomorrowOnly.getTime()) {
        prefix = '明天 ';
      } else if (dateOnly.getTime() === yesterdayOnly.getTime()) {
        prefix = '昨天 ';
      } else {
        prefix = format(date, 'M月d日 ');
      }

      return prefix + format(date, 'HH:mm');
    } catch {
      return dateStr;
    }
  };

  // 计算结束时间
  const calculateEndTime = (startAt: string, cost: number) => {
    try {
      const startDate = parseISO(startAt);
      const endDate = new Date(startDate.getTime() + cost * 1000);
      return endDate.toISOString();
    } catch {
      return null;
    }
  };

  // 获取时间标签内容
  const getTimeLabel = () => {
    const { startAt, cost, dueAt } = formData;

    if (!startAt && !dueAt) return null;

    let content = '';

    if (startAt && cost) {
      const endAt = calculateEndTime(startAt, cost);
      if (endAt) {
        const startFormatted = formatTime(startAt);
        const endFormatted = formatTime(endAt);
        // 安全地提取时间部分
        const startParts = startFormatted.split(' ');
        const endParts = endFormatted.split(' ');
        if (startParts.length >= 2 && endParts.length >= 2) {
          content = `${startParts[0]} ${startParts[1]}-${endParts[1]}`;
        } else {
          content = `${startFormatted}-${endFormatted}`;
        }
      } else {
        content = formatTime(startAt);
      }
    } else if (startAt) {
      content = formatTime(startAt);
    }

    if (dueAt) {
      const dueFormatted = formatTime(dueAt);
      const dueText = `⚠️ ${dueFormatted}截止`;
      content = content ? `${content} ${dueText}` : dueText;
    }

    return content;
  };

  // 获取提醒标签内容
  const getReminderLabel = () => {
    const reminders = formData.reminders as Reminder[] | undefined;
    if (!reminders?.length) return null;

    if (reminders.length === 1) {
      const reminder = reminders[0];
      if (reminder.trigger.type === 'RELATIVE' && reminder.trigger.offsetSeconds !== undefined) {
        const minutes = Math.abs(reminder.trigger.offsetSeconds) / 60;
        if (reminder.trigger.offsetSeconds < 0) {
          return `提前${minutes}分钟`;
        } else {
          return `延后${minutes}分钟`;
        }
      }
      return '1个提醒';
    }

    if (reminders.length <= 3) {
      const firstReminder = reminders[0];
      let firstDesc = '提醒';
      if (firstReminder.trigger.type === 'RELATIVE' && firstReminder.trigger.offsetSeconds !== undefined) {
        const minutes = Math.abs(firstReminder.trigger.offsetSeconds) / 60;
        if (firstReminder.trigger.offsetSeconds < 0) {
          firstDesc = `提前${minutes}分钟`;
        } else {
          firstDesc = `延后${minutes}分钟`;
        }
      }
      return `${firstDesc}+${reminders.length - 1}个`;
    }

    return `${reminders.length}个提醒`;
  };

  // 获取重复标签内容
  const getRecurrenceLabel = () => {
    const { recurrenceRule } = formData;
    if (!recurrenceRule) return null;

    switch (recurrenceRule) {
      case 'FREQ=DAILY':
        return '每天';
      case 'FREQ=WEEKLY':
        return '每周';
      case 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR':
        return '工作日';
      case 'FREQ=MONTHLY':
        return '每月';
      default:
        return '重复';
    }
  };

  // 获取重要性标签内容
  const getImportanceLabel = () => {
    const { vital, urgent, fixed } = formData;
    if (vital && urgent && fixed) return '重要紧急固定';
    if (vital && urgent) return '重要紧急';
    if (vital && fixed) return '重要固定';
    if (urgent && fixed) return '紧急固定';
    if (vital) return '重要';
    if (urgent) return '紧急';
    if (fixed) return '固定';
    return null;
  };

  // 标签基础样式
  const tagBaseClass = "flex items-center px-3 py-2 rounded-ios-lg text-ios-footnote font-medium transition-all duration-200";
  const tagActiveClass = "bg-ios-blue/10 dark:bg-ios-dark-blue/20 text-ios-blue dark:text-ios-dark-blue";
  const tagInactiveClass = "bg-ios-gray6 dark:bg-ios-dark-gray5 text-ios-gray dark:text-ios-dark-gray";

  const timeLabel = getTimeLabel();
  const reminderLabel = getReminderLabel();
  const recurrenceLabel = getRecurrenceLabel();
  const importanceLabel = getImportanceLabel();

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* 时间标签 */}
      <button
        onClick={onTimeClick}
        className={`${tagBaseClass} ${timeLabel ? tagActiveClass : tagInactiveClass}`}
      >
        <span className="mr-1">📅</span>
        {timeLabel || '时间'}
      </button>

      {/* 提醒标签 */}
      <button
        onClick={onReminderClick}
        className={`${tagBaseClass} ${reminderLabel ? tagActiveClass : tagInactiveClass}`}
      >
        <span className="mr-1">🔔</span>
        {reminderLabel || '提醒'}
      </button>

      {/* 重复标签 */}
      <button
        onClick={onRecurrenceClick}
        className={`${tagBaseClass} ${recurrenceLabel ? tagActiveClass : tagInactiveClass}`}
      >
        <span className="mr-1">🔄</span>
        {recurrenceLabel || '重复'}
      </button>

      {/* 重要性标签 */}
      <button
        onClick={onImportanceClick}
        className={`${tagBaseClass} ${importanceLabel ? tagActiveClass : tagInactiveClass}`}
      >
        <span className="mr-1">⚡</span>
        {importanceLabel || '重要'}
      </button>

      {/* 删除按钮 - 仅在编辑模式显示 */}
      {task && onDeleteClick && (
        <button
          onClick={onDeleteClick}
          className={`${tagBaseClass} bg-ios-red/10 dark:bg-ios-dark-red/20 text-ios-red dark:text-ios-dark-red`}
        >
          <span className="mr-1">{t('v3/tasks:actions.deleteIcon', '🗑️')}</span>
          {t('v3/tasks:actions.delete', '删除')}
        </button>
      )}
    </div>
  );
};
